import { secureSendApiClient } from 'myNet'

interface RecipientItem {
  email: string
  name?: string
}

export interface AttachmentItem {
  filename: string
  content_base64: string
  content_type?: string
  content_id?: string
}

interface SecureMailOptions {
  expires_at?: string
}

export interface SendSecureMailData {
  sender_email: string
  sender_name?: string
  recipient_list: RecipientItem[]
  cc_list?: RecipientItem[]
  bcc_list?: RecipientItem[]
  subject: string
  body_html: string
  attachments?: AttachmentItem[]
  options: SecureMailOptions
  secure_send_enabled: boolean
  read_receipt: boolean
  /** 회신 시 부모 메일의 Message-ID (수신 측 스레딩용). 서버가 꺾쇠를 정규화한다. */
  in_reply_to?: string
  /** 회신 시 조상~부모 Message-ID 목록 (dedup·50개 트리밍 완료). */
  references?: string[]
}

interface SendSecureMailResponse {
  secure_mail_id: string
  status: string
  link_url: string
  message_id: string
  sent_count: number
  failed_recipients: { email: string; reason: string }[]
  created_at: string
}

export interface SecureMailStatusResponse {
  is_secure_mail: boolean
  expired_date: string | null
}

interface SendEMLOptions {
  expires_at?: string
}

const secureSendApiHelper = {
  // POST /api/v1/secure-mail/send - Send secure mail
  sendSecureMail: async (data: SendSecureMailData): Promise<SendSecureMailResponse> => {
    const res = await secureSendApiClient.post('/api/v1/secure-mail/send', data)
    return res.data
  },

  // POST /api/v1/secure-mail/send-eml - Send secure mail via EML file
  sendSecureMailEml: async (
    emlFile: File,
    options: SendEMLOptions,
  ): Promise<SendSecureMailResponse> => {
    const formData = new FormData()

    formData.append('eml_file', emlFile, emlFile.name)
    formData.append('options', JSON.stringify(options))

    const res = await secureSendApiClient.postFormData('/api/v1/secure-mail/send-eml', formData)
    return res.data
  },

  // GET /api/v1/secure-mail/{secure_mail_id}/status - Get secure mail status
  getSecureMailStatus: async (secureMailId: string): Promise<SecureMailStatusResponse> => {
    const res = await secureSendApiClient.get(`/api/v1/secure-mail/${secureMailId}/status`)
    if (res.data != null) {
      const { code, data } = res.data
      if (code === 200) {
        return data
      }
    }
    throw new Error('Failed to get the status of the secure email.')
  },

  // PATCH /api/v1/secure-mail/{secure_mail_id}/renew - Renew secure mail access
  renewSecureMailAccess: async (secureMailId: string): Promise<boolean> => {
    const res = await secureSendApiClient.patch(`/api/v1/secure-mail/${secureMailId}/renew`)
    return res.data
  },

  // DELETE /api/v1/secure-mail/{secure_mail_id} - Remove secure mail access
  removeSecureMailAccess: async (secureMailId: string): Promise<boolean> => {
    const res = await secureSendApiClient.delete(`/api/v1/secure-mail/${secureMailId}`)
    return res.data
  },
}

export default secureSendApiHelper
